import { useState } from "react";
import { Link } from "wouter";
import { Plus, Trash2, Users } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OutreachTabs } from "@/components/outreach/OutreachTabs";
import { SegmentBuilder } from "@/components/newsletter/SegmentBuilder";
import { useNewsletterLists, useSaveList, useDeleteList, type NewsletterListRow } from "@/hooks/useNewsletter";
import type { SegmentRules } from "@shared/newsletterTypes";

export default function NewsletterLists() {
  const { data: lists = [] } = useNewsletterLists();
  const saveList = useSaveList();
  const deleteList = useDeleteList();
  const [editing, setEditing] = useState<Partial<NewsletterListRow> | null>(null);

  return (
    <AppLayout title="Newsletter lists">
      <div className="space-y-4 page-enter">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <OutreachTabs active="newsletter" />
          <div className="flex gap-2">
            <Link href="/campaigns/newsletter"><Button variant="outline">Issues</Button></Link>
            <Button className="gap-1" onClick={() => setEditing({ name: "", rules: {} })}>
              <Plus className="w-4 h-4" /> New list
            </Button>
          </div>
        </div>

        {lists.length === 0 ? (
          <div className="text-center py-14 border rounded-lg">
            <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium">No lists yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              A list is a set of rules over the buyer network, not a fixed roster. Buyers you add later match automatically.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg divide-y">
            {lists.map((l) => (
              <div key={l.id} className="flex items-center gap-4 px-4 py-3">
                <button className="min-w-0 flex-1 text-left" onClick={() => setEditing(l)}>
                  <p className="font-medium truncate">{l.name}</p>
                  <p className="text-sm text-muted-foreground tabular-nums">
                    {l.buyerCount} buyers / {l.contactCount} contacts
                  </p>
                </button>
                <Button variant="ghost" size="icon" aria-label="Delete list" onClick={() => deleteList.mutate(l.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing?.id ? "Edit list" : "New list"}</DialogTitle></DialogHeader>
            {editing && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="list-name">Name</Label>
                  <Input id="list-name" defaultValue={editing.name ?? ""}
                    onBlur={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Healthcare PE, lower middle market" />
                </div>
                <SegmentBuilder
                  rules={(editing.rules ?? {}) as SegmentRules}
                  onChange={(rules) => setEditing({ ...editing, rules })}
                />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!editing?.name?.trim()) return;
                  await saveList.mutateAsync({
                    id: editing.id,
                    name: editing.name,
                    rules: (editing.rules ?? {}) as SegmentRules,
                  });
                  setEditing(null);
                }}
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
